import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useCookies } from "react-cookie";
import { useTranslate } from "react-redux-multilingual";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faVolumeUp, faVolumeMute, faStop, faPaperclip, faTimes, faFilePdf, faImage, faDownload, faBrain, faTrash, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { IsAuthContext } from "app/App";
import {
    sendChatbotMessage, sendChatbotToolResult, readCurrentPage, highlightOnPage, clearHighlight, fetchChatbotUsage, unlockChatbotOverage,
    preparerPiece, telechargerFichier, MAX_PIECES, fetchChatbotConsignes, deleteChatbotConsigne, MEMORY_EVENT_KEYS,
    TOOL_READ_PAGE, TOOL_HIGHLIGHT, TOOL_ARTICLE_UPDATE, TOOL_ARTICLE_EXPORT, TOOL_FAMILLE_WRITE, TOOL_GENERATE_CHART,
} from "./chatbotIaService";
import ChatbotChartModal from "./ChatbotChartModal";

// Limites alignées sur la validation du backend (ChatbotController)
const MAX_HISTORY = 20;
const MAX_MESSAGE_LENGTH = 4000;

// Nombre de lignes au-delà duquel la zone de saisie cesse de grandir et se met à défiler
const MAX_INPUT_ROWS = 5;

// Relances consécutives sans aucune parole reconnue avant d'abandonner la dictée (≈ micro oublié ouvert)
const MAX_SILENT_RESTARTS = 6;

// Langue de reconnaissance vocale selon la langue de l'interface
const SPEECH_LANGS = { fr: "fr-FR", en: "en-US", es: "es-ES" };

// Silence (ms) après lequel un point est inséré automatiquement dans le texte dicté
const PAUSE_POINT_MS = 1000;

/**
 * Construit une expression qui reconnaît un mot ou une expression dictée, isolé du reste du texte
 * (pas de \b : il gère mal les lettres accentuées). Consomme les espaces qui l'entourent.
 */
const motDicte = (expression) => new RegExp("\\s*(?:^|\\s)(?:" + expression + ")(?![\\p{L}])\\s*", "giu");

// Ponctuation prononcée → signe, par langue. « point » seul est volontairement absent : c'est aussi un
// vrai mot (« point de vente »), le point vient de la pause (PAUSE_POINT_MS).
const PONCTUATIONS = {
    fr: [
        [motDicte("point d[’']interrogation"), "?"],
        [motDicte("point d[’']exclamation"), "!"],
        [motDicte("virgule"), ","],
        [motDicte("à la ligne"), "\n"],
    ],
    en: [
        [motDicte("question mark"), "?"],
        [motDicte("exclamation (?:mark|point)"), "!"],
        [motDicte("comma"), ","],
        [motDicte("new line"), "\n"],
    ],
    es: [
        [motDicte("signo de interrogación"), "?"],
        [motDicte("signo de exclamación"), "!"],
        [motDicte("coma"), ","],
        [motDicte("nueva línea|salto de línea"), "\n"],
    ],
};

// Commandes vocales, par langue : « arrêt micro » coupe la dictée, « envoie IA » envoie le message.
// Une formule inhabituelle exprès, pour ne pas se déclencher dans une phrase normale (« envoie-moi… »).
// Les variantes couvrent les transcriptions courantes de « IA » (ia, i a, i.a., ai).
const COMMANDES = {
    fr: { stop: motDicte("arr[êe]te?[\\s-]*micro(?:phone)?"), envoi: motDicte("envo(?:ie|i|ies|yer)[\\s-]*(?:i[\\s.-]*a|ai|aïe)") },
    en: { stop: motDicte("stop[\\s-]*mic(?:rophone)?"), envoi: motDicte("send[\\s-]*(?:a[\\s.-]*i|ia)") },
    es: { stop: motDicte("stop[\\s-]*micro(?:fono)?"), envoi: motDicte("env[ií]a[\\s-]*(?:i[\\s.-]*a|ai)") },
};

/**
 * Cherche une commande vocale dans le texte dicté. Renvoie { type: "stop"|"envoi", index } pour la
 * première trouvée (index = début de la commande dans le texte), ou null.
 */
const detecterCommande = (dicte, lang) => {
    let trouvee = null;
    Object.entries(COMMANDES[lang]).forEach(([type, re]) => {
        re.lastIndex = 0;
        const m = re.exec(dicte);
        if (m && (!trouvee || m.index < trouvee.index)) trouvee = { type, index: m.index };
    });
    return trouvee;
};

/**
 * Assemble le texte saisi avant la dictée (`base`) et le texte reconnu (`dicte`) : remplace la
 * ponctuation prononcée par ses signes et met une majuscule en début de phrase.
 */
const composerTexte = (base, dicte, lang) => {
    let d = dicte;
    PONCTUATIONS[lang].forEach(([re, symbole]) => {
        re.lastIndex = 0;
        d = d.replace(re, symbole === "\n" ? "\n" : symbole + " ");
    });
    // Pas de point derrière une ponctuation parlée (« virgule » suivi d'une pause donnerait « , . »)
    d = d.replace(/([,.?!;:])[ ]+\.(?=\s|$)/g, "$1");
    // Majuscule après . ? ! ou un retour à la ligne
    d = d.replace(/([.!?]\s+|\n)(\p{L})/gu, (m, avant, lettre) => avant + lettre.toUpperCase());
    let debut = base;
    if (/^\s*[,.?!]/.test(d)) debut = debut.replace(/[ \t]+$/, "");
    else if (/\s$/.test(debut)) d = d.replace(/^[ \t]+/, "");
    // Majuscule en tête si la phrase commence ici
    if (!debut.trim() || /[.!?]\s*$|\n\s*$/.test(debut)) d = d.replace(/^(\s*)(\p{L})/u, (m, esp, lettre) => esp + lettre.toUpperCase());
    return debut + d;
};

/**
 * Insère un point à chaque pause mémorisée dans le texte dicté. Une pause est mémorisée comme le début
 * du texte tel qu'il était au moment du silence ({ texte }) : le moteur peut ensuite corriger les derniers
 * mots, on retrouve donc l'endroit par le plus long début commun, coupé à la fin d'un mot entier.
 * Le point n'est pas ajouté si le texte se termine déjà par une ponctuation.
 */
const insererPoints = (dicte, pauses) => {
    const positions = pauses.map(({ texte }) => {
        // Même début ET fin de mot : sinon le moteur a corrigé le dernier mot (« peu » devenu « peux »)
        if (dicte.startsWith(texte) && (dicte.length === texte.length || /\s/.test(dicte[texte.length]))) return texte.length;
        let k = 0;
        while (k < texte.length && k < dicte.length && dicte[k] === texte[k]) k++;
        return dicte.lastIndexOf(" ", k);
    }).filter((pos) => pos > 0);
    // De la fin vers le début, pour que chaque insertion ne décale pas les positions suivantes
    [...new Set(positions)].sort((a, b) => b - a).forEach((pos) => {
        const avant = dicte.slice(0, pos).trimEnd();
        const apres = dicte.slice(pos).trimStart();
        dicte = avant + (/[.!?…:;,]$/.test(avant) ? " " : ". ") + apres;
    });
    return dicte;
};

// Clé du navigateur où est mémorisé le réglage « lire les réponses à voix haute » (désactivé par défaut)
const SPEAK_STORAGE_KEY = "chatbotIaSpeak";

// Clé renvoyée par le backend quand tout ce qui est autorisé à la société pour le mois est consommé
// (doit rester identique à ChatbotQuotaGestion::BLOCAGE_GLOBAL)
const QUOTA_GLOBAL_KEY = "chatbotIaQuotaGlobal";

/** Pourcentage de la jauge, une décimale au plus, au format du navigateur. */
const formatPercent = (value) => Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 });

// Taille maximale d'un morceau lu d'un bloc : Chrome interrompt sans erreur les lectures d'environ 15 s
const MAX_SPEECH_CHUNK = 200;

/** Vrai si le navigateur sait lire un texte à voix haute. */
const speechSynthesisSupported = () =>
    typeof window !== "undefined" && "speechSynthesis" in window && typeof window.SpeechSynthesisUtterance !== "undefined";

/** Retire du texte de Claude ce qui ne se lit pas : Markdown, puces, adresses internet. */
const nettoyerPourLecture = (texte) =>
    texte
        .replace(/https?:\/\/\S+/g, "")
        .replace(/[`*_#>]/g, " ")
        .replace(/^\s*[-•]\s+/gm, "")
        .replace(/[ \t]+/g, " ")
        .trim();

/**
 * Découpe le texte en morceaux de phrases d'au plus MAX_SPEECH_CHUNK caractères. Une ponctuation n'y
 * coupe que si elle est suivie d'un espace : « 3.5 » ou « 11 000,50 » restent entiers.
 */
const decouperPourLecture = (texte) => {
    const phrases = (texte.match(/[^\n]+?(?:[.!?;:](?=\s|$)|$)/g) || []).map((p) => p.trim()).filter(Boolean);
    const morceaux = [];
    phrases.forEach((p) => {
        const dernier = morceaux[morceaux.length - 1];
        if (dernier && (dernier + " " + p).length <= MAX_SPEECH_CHUNK) morceaux[morceaux.length - 1] = dernier + " " + p;
        else morceaux.push(p);
    });
    // Une phrase interminable est coupée aux espaces
    return morceaux.flatMap((m) => (m.length > MAX_SPEECH_CHUNK ? m.match(/.{1,200}(?:\s|$)/g) || [m] : [m]));
};

/** Choisit une voix du navigateur dans la langue de l'interface (région exacte d'abord), sinon null. */
const choisirVoix = (lang) => {
    const voix = window.speechSynthesis.getVoices();
    const code = SPEECH_LANGS[lang].toLowerCase();
    const norm = (v) => v.lang.replace("_", "-").toLowerCase();
    return voix.find((v) => norm(v) === code) || voix.find((v) => norm(v).startsWith(lang)) || null;
};

/** Renvoie le constructeur de reconnaissance vocale du navigateur (préfixé sur Safari/iOS), ou null. */
const getSpeechRecognition = () =>
    typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition || null : null;

// Phrase d'annonce du récapitulatif, par action du catalogue des familles (ChatbotEcritureGestion::preparer_familles)
const FAMILLE_RECAP_KEYS = {
    creer: "chatbotIaFamilleProposedCreer",
    renommer: "chatbotIaFamilleProposedRenommer",
    archiver: "chatbotIaFamilleProposedArchiver",
    desarchiver: "chatbotIaFamilleProposedDesarchiver",
};

/**
 * Met en gras les passages **entre doubles astérisques** d'une ligne (sans HTML injecté).
 *
 * @param {string} line - Ligne de texte de Claude
 * @returns {Array} Morceaux de texte et éléments <strong>
 */
const renderInline = (line) =>
    line.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
        part.length > 4 && part.startsWith("**") && part.endsWith("**") ? <strong key={i}>{part.slice(2, -2)}</strong> : part
    );

/**
 * Affiche le texte de Claude avec le Markdown simple qu'il utilise : gras, listes à puces, titres (rendus en gras).
 * Volontairement limité : ni images ni liens cliquables (voir decisions.md, communication vers l'extérieur).
 *
 * @param {string} text - Réponse de Claude
 * @returns {Array} Paragraphes et listes
 */
const renderRichText = (text) => {
    const out = [];
    let paragraph = [];
    let list = [];
    const flushParagraph = () => {
        if (paragraph.length) {
            out.push(<p key={out.length} className="chatbot-ia-text">{paragraph.map((l, i) => <React.Fragment key={i}>{i > 0 && "\n"}{renderInline(l)}</React.Fragment>)}</p>);
            paragraph = [];
        }
    };
    const flushList = () => {
        if (list.length) {
            out.push(<ul key={out.length} className="chatbot-ia-list">{list.map((item, i) => <li key={i}>{renderInline(item)}</li>)}</ul>);
            list = [];
        }
    };
    text.split("\n").forEach((line) => {
        const bullet = line.match(/^\s*[-*•]\s+(.*)$/);
        const heading = line.match(/^\s*#{1,6}\s+(.*)$/);
        if (bullet) {
            flushParagraph();
            list.push(bullet[1]);
        } else if (line.trim() === "") {
            // Ligne vide : fin de paragraphe
            flushParagraph();
            flushList();
        } else {
            flushList();
            paragraph.push(heading ? `**${heading[1]}**` : line);
        }
    });
    flushParagraph();
    flushList();
    return out;
};

/**
 * Accepte uniquement les routes internes de l'application.
 * Protège contre une route externe injectée via des données (voir decisions.md).
 *
 * @param {string} route - Route demandée par le bot
 * @returns {boolean} true si la route est interne
 */
const isInternalRoute = (route) => typeof route === "string" && /^\/(?!\/)/.test(route);

/**
 * Fenêtre de chat de l'assistant IA (bulle flottante en bas à droite, agrandissable).
 *
 * Affichée uniquement si l'option `general.chatbotIa` est active pour la société de
 * l'utilisateur et si l'utilisateur n'est pas restreint (access-configuration). Montée dans GullLayout : l'historique survit à la navigation entre pages.
 * Les réponses viennent du proxy Laravel (api/chatbot/message) via chatbotIaService.
 */
const ChatbotIa = () => {
    // ─── State & refs ─────────────────────────────────────────────────────────
    const t = useTranslate();
    const history = useHistory();
    // Langue de l'utilisateur dans Freebird (même cookie que l'export CSV) : le chatbot répond dans
    // cette langue, jamais toujours en français (voir docs/chatbot-ia/decisions.md).
    const [cookies] = useCookies(["lang"]);
    const lang = ["fr", "en", "es"].includes(cookies.lang) ? cookies.lang : "fr";
    const { authParam } = useContext(IsAuthContext);
    const [open, setOpen] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [messages, setMessages] = useState([]); // { id, from: 'user'|'bot', text?, pieces?, blocks?, decisions }
    const [input, setInput] = useState("");
    // Pièces jointes choisies mais pas encore envoyées ({ id, nom, type, data }), et erreur de sélection
    const [pieces, setPieces] = useState([]);
    const [pieceError, setPieceError] = useState("");
    const fileInputRef = useRef(null);
    // Pièces envoyées avec le dernier message : à renvoyer avec chaque résultat d'outil de cette question,
    // le backend les retirant du jeton d'attente (voir sendChatbotToolResult)
    const piecesEnvoyeesRef = useRef([]);
    const [waiting, setWaiting] = useState(false);
    // Graphique de vente affiché dans la modale (voir renderBlock, type "chart") : reste en mémoire pour
    // le lien de réouverture, perdu comme le reste de la conversation au rechargement de la page
    const [activeChart, setActiveChart] = useState(null);
    const bottomRef = useRef(null);
    const nextId = useRef(1);
    const inputRef = useRef(null);
    const recognitionRef = useRef(null);
    const dictationWanted = useRef(false); // vrai tant que l'utilisateur n'a pas arrêté la dictée
    const restartsSansResultat = useRef(0);
    const latestInputRef = useRef("");
    const pauseTimerRef = useRef(null); // chronomètre du point automatique après un silence
    const handleSendRef = useRef(null); // dernière version de handleSend, pour les commandes vocales
    const [listening, setListening] = useState(false);
    const [micError, setMicError] = useState("");
    // Lecture des réponses à voix haute : réglage mémorisé dans le navigateur, désactivé par défaut
    const [speakReplies, setSpeakReplies] = useState(() => {
        try { return window.localStorage.getItem(SPEAK_STORAGE_KEY) === "1"; } catch (err) { return false; }
    });
    const [speaking, setSpeaking] = useState(false);
    const speakRepliesRef = useRef(speakReplies); // lu par processResponse, dont la closure peut être périmée
    const openRef = useRef(false);
    openRef.current = open;

    // Jauge du pack IA : chargée à l'ouverture du chat, puis remplacée par celle que le backend joint
    // à chaque réponse (null tant qu'elle n'est pas connue, et la jauge n'est alors pas affichée)
    const [usage, setUsage] = useState(null);
    // Déblocage d'un dépassement (utilisateurs autorisés par Freebird, quota de la société atteint)
    const [unlockAmount, setUnlockAmount] = useState("");
    const [unlocking, setUnlocking] = useState(false);
    const [unlockError, setUnlockError] = useState("");
    // Mémoire de l'assistant (consignes que l'utilisateur lui a demandé de retenir) : affichée à la place
    // de la conversation. memory = { consignes, max }, null tant que la liste n'est pas chargée.
    const [memoryOpen, setMemoryOpen] = useState(false);
    const [memory, setMemory] = useState(null);
    const [memoryError, setMemoryError] = useState("");
    const [memoryBusy, setMemoryBusy] = useState(false);

    useEffect(() => {
        if (!open) return undefined;
        let actif = true;
        fetchChatbotUsage().then((data) => { if (actif && data) setUsage(data); });
        return () => { actif = false; };
    }, [open]);

    // Fait défiler la conversation vers le dernier message
    useEffect(() => {
        if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }, [messages, waiting, open]);

    // Ajuste la hauteur de la zone de saisie à son contenu, jusqu'à MAX_INPUT_ROWS lignes (au-delà,
    // elle défile). Un <textarea> ne s'adapte pas tout seul : on remet sa hauteur à "auto" avant de
    // lire scrollHeight, sinon la hauteur précédente l'empêcherait de rétrécir quand on efface du texte.
    useEffect(() => {
        const zone = inputRef.current;
        if (!zone) return;
        const style = window.getComputedStyle(zone);
        const ligne = parseFloat(style.lineHeight) || 20;
        // scrollHeight compte le contenu et les paddings mais jamais les bordures : en box-sizing
        // border-box (réglage global de Bootstrap), il faut les rajouter à la hauteur posée.
        const bordures = style.boxSizing === "border-box"
            ? parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth) : 0;
        const maxi = ligne * MAX_INPUT_ROWS + parseFloat(style.paddingTop) + parseFloat(style.paddingBottom) + bordures;
        zone.style.height = "auto";
        zone.style.height = Math.min(zone.scrollHeight + bordures, maxi) + "px";
        // Pendant la dictée, le texte arrive sans que le curseur de saisie ne bouge : le navigateur ne fait
        // alors pas défiler la zone, contrairement à la frappe. On suit donc la fin du texte à la main.
        if (listening) zone.scrollTop = zone.scrollHeight;
        // memoryOpen : la zone de saisie est démontée pendant l'affichage de la mémoire, puis remontée
    }, [input, open, expanded, listening, memoryOpen]);

    // ─── Dictée vocale ────────────────────────────────────────────────────────
    /**
     * Lance une session de reconnaissance et la relance d'elle-même tant que l'utilisateur n'a pas arrêté
     * la dictée (second clic sur le micro) ni envoyé le message. Le navigateur coupe seul la reconnaissance
     * après quelques secondes de silence (surtout sur Safari iOS, où le mode continu n'est pas fiable) :
     * on la redémarre alors, en reprenant le texte déjà présent dans la zone de saisie comme base.
     * Le texte reconnu n'est jamais envoyé seul : l'utilisateur relit avant d'envoyer.
     */
    const startRecognition = () => {
        const Reco = getSpeechRecognition();
        if (!Reco) return;
        const reco = new Reco();
        reco.lang = SPEECH_LANGS[lang];
        reco.interimResults = true;
        reco.continuous = true;
        // Texte déjà saisi avant cette session, conservé devant le texte reconnu
        const saisi = latestInputRef.current;
        const base = saisi && !/\s$/.test(saisi) ? saisi + " " : saisi;
        // Pauses repérées pendant cette session (voir insererPoints) et dernier texte reconnu
        const pauses = [];
        let derniereDicte = "";
        reco.onresult = (e) => {
            // Résultat tardif d'une session déjà arrêtée (clic micro, envoi) : ignoré
            if (!dictationWanted.current) return;
            restartsSansResultat.current = 0;
            const dicte = Array.from(e.results).map((r) => r[0].transcript).join("");
            derniereDicte = dicte;
            const commande = detecterCommande(dicte, lang);
            if (commande) {
                // Le texte avant la commande est gardé, la commande elle-même est retirée
                const avant = dicte.slice(0, commande.index);
                const texte = composerTexte(base, insererPoints(avant, pauses), lang).trim().slice(0, MAX_MESSAGE_LENGTH);
                latestInputRef.current = texte;
                setInput(texte);
                stopDictation();
                if (commande.type === "envoi" && handleSendRef.current) handleSendRef.current(texte);
                return;
            }
            const texte = composerTexte(base, insererPoints(dicte, pauses), lang).slice(0, MAX_MESSAGE_LENGTH);
            latestInputRef.current = texte;
            setInput(texte);
            // Point automatique : après PAUSE_POINT_MS sans nouveau mot reconnu, on mémorise la pause et on
            // affiche le point. La session n'est JAMAIS coupée ni relancée ici : un redémarrage perdait les
            // premiers mots dits juste après la pause (et ceux pas encore reconnus au moment de la coupure).
            clearTimeout(pauseTimerRef.current);
            pauseTimerRef.current = setTimeout(() => {
                if (!dictationWanted.current || !derniereDicte.trim()) return;
                // Même pause déjà mémorisée : rien à faire
                if (pauses.length && pauses[pauses.length - 1].texte === derniereDicte) return;
                pauses.push({ texte: derniereDicte });
                const avecPoint = composerTexte(base, insererPoints(derniereDicte, pauses), lang).slice(0, MAX_MESSAGE_LENGTH);
                latestInputRef.current = avecPoint;
                setInput(avecPoint);
            }, PAUSE_POINT_MS);
        };
        reco.onerror = (e) => {
            // "no-speech" / "aborted" : silence ou arrêt volontaire, la relance dans onend s'en charge
            if (e.error === "not-allowed" || e.error === "service-not-allowed") {
                dictationWanted.current = false;
                setMicError(t("chatbotIaMicDenied"));
            } else if (e.error !== "no-speech" && e.error !== "aborted") {
                dictationWanted.current = false;
                setMicError(t("chatbotIaMicError"));
            }
        };
        reco.onend = () => {
            recognitionRef.current = null;
            // Plusieurs sessions d'affilée sans un mot reconnu : on abandonne plutôt que de boucler
            if (dictationWanted.current && restartsSansResultat.current < MAX_SILENT_RESTARTS) {
                restartsSansResultat.current += 1;
                // Court délai : relancer dans la foulée du onend fait échouer start() sur certains navigateurs
                setTimeout(() => {
                    if (dictationWanted.current && !recognitionRef.current) startRecognition();
                }, 100);
            } else {
                dictationWanted.current = false;
                setListening(false);
            }
        };
        recognitionRef.current = reco;
        try {
            reco.start();
        } catch (err) {
            recognitionRef.current = null;
            dictationWanted.current = false;
            setListening(false);
            setMicError(t("chatbotIaMicError"));
        }
    };

    /** Arrête la dictée pour de bon (aucune relance) ; abort() écarte les résultats encore en route. */
    const stopDictation = () => {
        clearTimeout(pauseTimerRef.current);
        dictationWanted.current = false;
        setListening(false);
        if (recognitionRef.current) recognitionRef.current.abort();
    };

    /** Clic sur le micro : démarre la dictée, ou l'arrête si elle est en cours. */
    const toggleDictation = () => {
        if (dictationWanted.current) {
            stopDictation();
            return;
        }
        if (!getSpeechRecognition()) return;
        // Le micro entendrait la voix de l'assistant : on la coupe avant d'écouter
        cancelSpeech();
        unlockSpeech();
        setMicError("");
        dictationWanted.current = true;
        restartsSansResultat.current = 0;
        setListening(true);
        startRecognition();
    };

    // Garde la dernière valeur saisie accessible aux relances (la closure de startRecognition est périmée)
    useEffect(() => {
        latestInputRef.current = input;
    }, [input]);

    // Coupe le micro à la fermeture de la fenêtre et au démontage
    useEffect(() => {
        if (!open) stopDictation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    useEffect(() => () => {
        clearTimeout(pauseTimerRef.current);
        dictationWanted.current = false;
        if (recognitionRef.current) recognitionRef.current.abort();
    }, []);

    // ─── Lecture des réponses à voix haute ────────────────────────────────────
    /** Interrompt toute lecture en cours. */
    const cancelSpeech = () => {
        if (speechSynthesisSupported()) window.speechSynthesis.cancel();
        setSpeaking(false);
    };

    /**
     * Lit un texte à voix haute dans la langue de l'interface, par morceaux (voir decouperPourLecture).
     * Les blocs autres que le texte de Claude (graphiques, récapitulatifs, sources) ne sont jamais lus.
     */
    const speak = (texte) => {
        if (!speechSynthesisSupported() || !texte) return;
        window.speechSynthesis.cancel();
        const morceaux = decouperPourLecture(nettoyerPourLecture(texte));
        if (!morceaux.length) return;
        const voix = choisirVoix(lang);
        morceaux.forEach((morceau, i) => {
            const u = new window.SpeechSynthesisUtterance(morceau);
            u.lang = SPEECH_LANGS[lang];
            if (voix) u.voice = voix;
            if (i === 0) u.onstart = () => setSpeaking(true);
            if (i === morceaux.length - 1) u.onend = () => setSpeaking(false);
            // Une annulation volontaire (nouvelle lecture, stop) n'est pas une erreur à traiter ici
            u.onerror = (e) => {
                if (e.error !== "interrupted" && e.error !== "canceled" && i === morceaux.length - 1) setSpeaking(false);
            };
            window.speechSynthesis.speak(u);
        });
    };

    /**
     * Débloque la synthèse vocale pendant un geste de l'utilisateur (clic) : Safari sur iPhone refuse sinon
     * de parler, la réponse arrivant bien après le clic. Lecture muette d'un espace.
     */
    const unlockSpeech = () => {
        if (!speechSynthesisSupported() || !speakRepliesRef.current) return;
        const u = new window.SpeechSynthesisUtterance(" ");
        u.volume = 0;
        window.speechSynthesis.speak(u);
    };

    /** Active ou désactive la lecture des réponses ; le choix est mémorisé dans le navigateur. */
    const toggleSpeakReplies = () => {
        const next = !speakReplies;
        setSpeakReplies(next);
        speakRepliesRef.current = next;
        try { window.localStorage.setItem(SPEAK_STORAGE_KEY, next ? "1" : "0"); } catch (err) { /* stockage indisponible : réglage valable pour la session */ }
        if (next) unlockSpeech();
        else cancelSpeech();
    };

    // Plus de voix quand la fenêtre se ferme ou que le composant disparaît
    useEffect(() => {
        if (!open) cancelSpeech();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    useEffect(() => () => {
        if (speechSynthesisSupported()) window.speechSynthesis.cancel();
    }, []);

    // ─── Garde d'activation ───────────────────────────────────────────────────
    // Règle de base : sans l'option chatbotIa pour la société, aucun chat (contrôlé aussi côté backend)
    if (!authParam?.general?.chatbotIa?.active) return null;
    // Utilisateur restreint (access-configuration, onglet Général) : pas de bulle.
    // Même règle que ChatbotGestion::is_restricted, qui reste le vrai verrou côté backend.
    const isRestricted = Array.isArray(authParam.access)
        && authParam.access.some((a) => a.isfor === "general" && a.action === "chatbotIa");
    if (isRestricted) return null;

    // ─── Handlers ─────────────────────────────────────────────────────────────

    /**
     * Construit l'historique envoyé au backend (le serveur ne stocke pas la conversation).
     * Les messages d'erreur sont exclus, l'historique est plafonné à MAX_HISTORY et doit commencer par l'utilisateur.
     * Les pièces jointes partent à côté, chacune avec l'index du message qui la porte dans cet
     * historique : un document reste donc lisible par l'assistant pendant toute la conversation
     * (le backend pose un point de cache dessus pour ne pas le repayer à chaque message).
     *
     * @returns {{history: Array<{role: string, content: string}>, fichiers: Array<{message: number, nom: string, type: string, data: string}>}}
     */
    const buildHistory = (all) => {
        const entries = all
            .filter((m) => !m.error)
            .map((m) => ({
                role: m.from === "user" ? "user" : "assistant",
                // Seul le texte part dans l'historique : mentions, sources et demandes d'autorisation relèvent de l'interface
                content: m.from === "user" ? m.text : m.blocks.filter((b) => b.type === "text").map((b) => b.text ?? t(b.key)).join("\n"),
                pieces: (m.from === "user" && m.pieces) || [],
            }))
            .filter((m) => m.content)
            .slice(-MAX_HISTORY);
        while (entries.length && entries[0].role !== "user") entries.shift();
        const fichiers = [];
        entries.forEach((entry, index) => entry.pieces.forEach((p) => fichiers.push({ message: index, nom: p.nom, type: p.type, data: p.data })));
        return { history: entries.map(({ role, content }) => ({ role, content })), fichiers };
    };

    /**
     * Traite une réponse du proxy et l'ajoute à la conversation.
     * lire_page, surligner et generer_graphique_vente sont exécutés automatiquement (autant de fois que
     * Claude le demande, plafonné côté backend) ; une demande d'autorisation de recherche web s'affiche
     * avec ses boutons et attend le clic de l'utilisateur.
     *
     * @param {Object|null} initial - Réponse de sendChatbotMessage / sendChatbotToolResult
     */
    const processResponse = async (initial) => {
        let response = initial;
        let pageRead = false;
        let highlighted = false;
        // Claude peut demander plusieurs graphiques pour une même question : chacun garde son lien
        const charts = [];
        while (response && response.pending && [TOOL_READ_PAGE, TOOL_HIGHLIGHT, TOOL_GENERATE_CHART].includes(response.pending.tool)) {
            const { id, tool, target, chart } = response.pending;
            let content;
            if (tool === TOOL_READ_PAGE) {
                pageRead = true;
                content = readCurrentPage();
            } else if (tool === TOOL_HIGHLIGHT) {
                highlighted = true;
                content = highlightOnPage(target);
            } else {
                // Le graphique est déjà calculé par le serveur (chart) : le front ne fait que le
                // renvoyer tel quel pour reprendre la conversation, sans rien recalculer ni retranscrire.
                charts.push(chart);
                content = JSON.stringify(chart);
            }
            response = await sendChatbotToolResult(id, { content }, piecesEnvoyeesRef.current);
        }
        // null = session expirée : l'intercepteur global redirige déjà vers la connexion
        if (!response) return;
        if (response.usage) setUsage(response.usage);

        const blocks = [];
        if (pageRead) blocks.push({ type: "info", key: "chatbotIaPageRead" });
        if (highlighted) blocks.push({ type: "info", key: "chatbotIaHighlighted" });
        charts.forEach((chart) => blocks.push({ type: "chart", chart }));
        if (response.pending) {
            const { id, tool, text, query, reason, write } = response.pending;
            if (text) blocks.push({ type: "text", text });
            // Écriture : le récapitulatif vient du serveur, pas du modèle. Le clic de l'utilisateur est le seul déclencheur.
            if ([TOOL_ARTICLE_UPDATE, TOOL_ARTICLE_EXPORT, TOOL_FAMILLE_WRITE].includes(tool)) blocks.push({ type: "write_approval", pendingId: id, write, tool });
            else blocks.push({ type: "search_approval", pendingId: id, query, reason });
        } else {
            blocks.push(...response.blocks);
        }
        setMessages((prev) => [...prev, { id: nextId.current++, from: "bot", blocks, error: response.error, decisions: {} }]);
        // Lecture à voix haute du seul texte de la réponse (fenêtre ouverte et réglage actif)
        if (speakRepliesRef.current && openRef.current && !response.error) {
            speak(blocks.filter((b) => b.type === "text" && b.text).map((b) => b.text).join("\n"));
        }
        // Ouverture automatique une fois la réponse complète prête (texte compris), pas pendant la
        // boucle d'outils. Sur plusieurs graphiques, le dernier s'ouvre ; les autres restent accessibles
        // par leur lien dans la conversation.
        if (charts.length) setActiveChart(charts[charts.length - 1]);
    };

    /**
     * Envoie le message saisi au proxy et ajoute la réponse du bot à la conversation.
     * Un document peut partir sans texte : une phrase neutre le remplace (le backend exige un texte).
     */
    const handleSend = async (text) => {
        const content = (text ?? input).trim() || (pieces.length ? t("chatbotIaFileDefaultText") : "");
        if (!content || waiting) return;
        // L'envoi met fin à la dictée et à la lecture de la réponse précédente
        stopDictation();
        cancelSpeech();
        unlockSpeech();
        const userMessage = { id: nextId.current++, from: "user", text: content, pieces };
        // L'entourage de la réponse précédente n'a plus lieu d'être
        clearHighlight();
        setMessages((prev) => [...prev, userMessage]);
        setInput("");
        setPieces([]);
        setPieceError("");
        setWaiting(true);
        const { history: historique, fichiers } = buildHistory([...messages, userMessage]);
        piecesEnvoyeesRef.current = fichiers;
        await processResponse(await sendChatbotMessage(historique, history.location.pathname, lang, fichiers));
        setWaiting(false);
    };
    handleSendRef.current = handleSend;

    // Pièces déjà envoyées dans la conversation : avec celles en attente, au plus MAX_PIECES
    const piecesEnvoyees = messages.reduce((total, m) => total + (m.pieces ? m.pieces.length : 0), 0);
    const piecesRestantes = MAX_PIECES - piecesEnvoyees - pieces.length;

    /**
     * Prépare les fichiers choisis avec le trombone (réduction des photos, contrôle du type et de la
     * taille, voir preparerPiece) et les ajoute aux pièces en attente d'envoi.
     */
    const handleFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        // Permet de rechoisir le même fichier après l'avoir retiré
        e.target.value = "";
        setPieceError("");
        let restantes = piecesRestantes;
        const ajout = [];
        for (const file of files) {
            if (restantes <= 0) {
                setPieceError(t("chatbotIaFileTooMany"));
                break;
            }
            const piece = await preparerPiece(file);
            if (piece.erreur) {
                setPieceError(t(piece.erreur));
                continue;
            }
            ajout.push({ id: nextId.current++, ...piece });
            restantes -= 1;
        }
        if (ajout.length) setPieces((prev) => [...prev, ...ajout]);
    };

    /** Retire une pièce jointe pas encore envoyée. */
    const removePiece = (id) => setPieces((prev) => prev.filter((p) => p.id !== id));

    /**
     * Transmet au backend la décision de l'utilisateur sur une recherche web proposée.
     * C'est ce clic, et lui seul, qui rend l'outil web_search disponible pour la question.
     */
    const handleSearchDecision = async (message, index, approved) => {
        if (waiting) return;
        handleDecision(message.id, index, approved ? "allowed" : "refused");
        setWaiting(true);
        await processResponse(await sendChatbotToolResult(message.blocks[index].pendingId, { approved }, piecesEnvoyeesRef.current));
        setWaiting(false);
    };

    /**
     * Transmet au backend la décision de l'utilisateur sur une modification proposée.
     * C'est ce clic, et lui seul, qui déclenche l'enregistrement : le backend n'écrit rien avant.
     */
    const handleWriteDecision = async (message, index, approved) => {
        if (waiting) return;
        handleDecision(message.id, index, approved ? "confirmed" : "cancelled");
        setWaiting(true);
        await processResponse(await sendChatbotToolResult(message.blocks[index].pendingId, { approved }, piecesEnvoyeesRef.current));
        setWaiting(false);
    };

    /**
     * Ouvre ou referme la mémoire de l'assistant. La liste est rechargée à chaque ouverture : elle a pu
     * changer depuis le chat (« souviens-toi que... », « oublie... ») ou depuis un autre poste.
     */
    const toggleMemory = async () => {
        if (memoryOpen) {
            setMemoryOpen(false);
            return;
        }
        setMemoryOpen(true);
        setMemory(null);
        setMemoryError("");
        const result = await fetchChatbotConsignes();
        // erreur vide = session expirée, déjà prise en charge par l'intercepteur global
        if (result.erreur !== undefined) setMemoryError(result.erreur);
        else setMemory(result);
    };

    /** Supprime une consigne de la mémoire ; l'assistant ne l'appliquera plus dès la question suivante. */
    const handleDeleteConsigne = async (id) => {
        if (memoryBusy) return;
        setMemoryBusy(true);
        setMemoryError("");
        const result = await deleteChatbotConsigne(id);
        setMemoryBusy(false);
        if (result.erreur !== undefined) setMemoryError(result.erreur);
        else setMemory(result);
    };

    /** Entrée envoie le message, Maj+Entrée insère un retour à la ligne. */
    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    /** Enregistre la décision (confirmée ou annulée) sur un bloc de confirmation. */
    const handleDecision = (messageId, blockIndex, decision) => {
        setMessages((prev) =>
            prev.map((m) => (m.id === messageId ? { ...m, decisions: { ...m.decisions, [blockIndex]: decision } } : m))
        );
    };

    /** Navigue vers une page interne demandée par le bot. */
    const handleLink = (route) => {
        if (isInternalRoute(route)) history.push(route);
    };

    // ─── Rendu d'un bloc de réponse ───────────────────────────────────────────

    /**
     * Récapitulatif d'une modification d'article (un ou plusieurs), avec la valeur avant/après de
     * chaque champ et l'avertissement de propagation en masse.
     *
     * @param {{articles: Array}} write - Contenu préparé par ChatbotEcritureGestion::preparer_modification
     */
    const renderWriteRecap = (write) => {
        const { articles } = write;
        return (
            <>
                <p className="chatbot-ia-text mb-1">
                    {articles.length > 1
                        ? t("chatbotIaWriteProposedMany").replace("{nb}", articles.length)
                        : t("chatbotIaWriteProposed")}
                </p>
                {articles.map((entry) => (
                    <div key={entry.article.id} className="chatbot-ia-write-article">
                        <p className="chatbot-ia-text mb-1">
                            <strong>{entry.article.nom}</strong>{entry.article.sku ? " (" + entry.article.sku + ")" : ""}
                        </p>
                        <ul className="chatbot-ia-write-list">
                            {entry.modifications.map((modification) => (
                                <li key={modification.champ}>
                                    <strong>{modification.champ}</strong> : {modification.avant || t("chatbotIaWriteEmpty")}
                                    {" → "}
                                    <strong>{modification.apres || t("chatbotIaWriteEmpty")}</strong>
                                    {/* Règle de mise à jour en masse de la société : la valeur sera recopiée sur d'autres articles */}
                                    {modification.propagation && (
                                        <div className="chatbot-ia-write-warning">
                                            <i className="i-Danger me-1"></i>
                                            {modification.propagation.articles
                                                ? t("chatbotIaWriteBulkCount").replace("{nb}", modification.propagation.articles).replace("{critere}", modification.propagation.critere)
                                                : t("chatbotIaWriteBulk").replace("{critere}", modification.propagation.critere)}
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </>
        );
    };

    /**
     * Récapitulatif d'un export CSV : nombre d'articles concernés, colonnes (toutes, ou la liste
     * précise d'une sélection partielle — jamais énumérées si « toutes », ça peut en compter des
     * dizaines), option apostrophe.
     *
     * @param {{total: number, nb_colonnes: number, toutes_colonnes: boolean, colonnes: ?Array, apostrophe: boolean}} write
     *        Contenu préparé par ChatbotEcritureGestion::preparer_export
     */
    const renderExportRecap = (write) => (
        <>
            <p className="chatbot-ia-text mb-1">{t("chatbotIaExportProposed")}</p>
            <ul className="chatbot-ia-write-list">
                <li>{t("chatbotIaExportTotal").replace("{nb}", write.total)}</li>
                <li>
                    {write.toutes_colonnes
                        ? t("chatbotIaExportColumnsAll")
                        : t("chatbotIaExportColumnsSome").replace("{nb}", write.nb_colonnes)}
                    {!write.toutes_colonnes && Array.isArray(write.colonnes) && write.colonnes.length > 0 && (
                        <> : {write.colonnes.join(", ")}</>
                    )}
                </li>
                {write.apostrophe && <li>{t("chatbotIaExportApostrophe")}</li>}
            </ul>
        </>
    );

    /**
     * Récapitulatif d'une action sur le catalogue des familles : l'action demandée, le niveau
     * concerné (avec son libellé métier, jamais son nom technique) et les valeurs visées.
     *
     * @param {{action: string, niveau: string, valeurs: Array}} write - Contenu préparé par
     *        ChatbotEcritureGestion::preparer_familles ; `detail` porte le nouveau nom d'un renommage.
     */
    const renderFamilleRecap = (write) => (
        <>
            <p className="chatbot-ia-text mb-1">
                {t(FAMILLE_RECAP_KEYS[write.action]).replace("{niveau}", write.niveau)}
            </p>
            <ul className="chatbot-ia-write-list">
                {write.valeurs.map((valeur) => (
                    <li key={valeur.nom}>
                        <strong>{valeur.nom}</strong>
                        {valeur.detail && <> {" → "} <strong>{valeur.detail}</strong></>}
                    </li>
                ))}
            </ul>
        </>
    );

    /**
     * Pastilles des pièces jointes (icône PDF ou image et nom du fichier).
     *
     * @param {Array} liste - Pièces { id, nom, type }
     * @param {Function} [onRemove] - Si fourni, chaque pastille a un bouton pour retirer la pièce (avant envoi)
     */
    const renderPieces = (liste, onRemove) => {
        if (!liste || !liste.length) return null;
        return (
            <div className="chatbot-ia-pieces">
                {liste.map((p) => (
                    <span key={p.id} className="chatbot-ia-piece" title={p.nom}>
                        <FontAwesomeIcon icon={p.type === "application/pdf" ? faFilePdf : faImage} />
                        <span>{p.nom}</span>
                        {onRemove && (
                            <button type="button" onClick={() => onRemove(p.id)} aria-label={t("chatbotIaAttachRemove")} title={t("chatbotIaAttachRemove")}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        )}
                    </span>
                ))}
            </div>
        );
    };

    /**
     * Débloque un dépassement pour le mois en cours, puis le confirme dans la conversation.
     * Le montant est saisi en prix de vente, dans la devise du pack ; le backend vérifie le droit.
     */
    const handleUnlock = async () => {
        const amount = Number(String(unlockAmount).replace(",", "."));
        if (!(amount > 0)) {
            setUnlockError("chatbotIaAmountInvalid");
            return;
        }
        setUnlocking(true);
        setUnlockError("");
        const result = await unlockChatbotOverage(amount);
        setUnlocking(false);
        // erreur vide = session expirée, déjà prise en charge par l'intercepteur global
        if (result.erreur !== undefined) {
            setUnlockError(result.erreur);
            return;
        }
        setUsage(result.usage);
        setUnlockAmount("");
        setMessages((prev) => [...prev, { id: nextId.current++, from: "bot", blocks: [{ type: "text", key: "chatbotIaUnlocked" }], decisions: {} }]);
    };

    /**
     * Formulaire de déblocage, sous le message « quota de la société atteint » : seulement sur le dernier
     * message, tant que le blocage dure, et pour un utilisateur autorisé à débloquer.
     */
    const renderUnlock = (message) => {
        const dernier = messages.length > 0 && messages[messages.length - 1].id === message.id;
        if (!dernier || !usage || !usage.peut_debloquer || usage.blocage !== QUOTA_GLOBAL_KEY) return null;
        return (
            <div className="chatbot-ia-unlock">
                <label htmlFor="chatbot-ia-unlock-amount">{t("chatbotIaUnlockLabel")}</label>
                <div className="chatbot-ia-unlock-row">
                    <input id="chatbot-ia-unlock-amount" type="number" min="0" step="any" className="form-control form-control-sm" value={unlockAmount} onChange={(e) => setUnlockAmount(e.target.value)} disabled={unlocking} />
                    <span>{usage.devise}</span>
                    <button type="button" className="btn btn-sm btn-primary" onClick={handleUnlock} disabled={unlocking}>{t("chatbotIaUnlockButton")}</button>
                </div>
                {unlockError && <small className="text-danger">{t(unlockError)}</small>}
            </div>
        );
    };

    /** Affiche un bloc (texte, mention, sources, autorisation de recherche, récapitulatif d'écriture, lien ou confirmation) d'un message du bot. */
    const renderBlock = (message, block, index) => {
        if (block.type === "text") {
            // text = réponse libre de Claude (Markdown simple), key = message applicatif traduit (erreur, pas de réponse)
            if (block.text) return <React.Fragment key={index}>{renderRichText(block.text)}</React.Fragment>;
            return (
                <React.Fragment key={index}>
                    <p className="chatbot-ia-text">{t(block.key)}</p>
                    {block.key === QUOTA_GLOBAL_KEY && renderUnlock(message)}
                </React.Fragment>
            );
        }
        if (block.type === "info") {
            return <p key={index} className="chatbot-ia-info"><i className="i-File-Search me-1"></i>{t(block.key)}</p>;
        }
        if (block.type === "sources") {
            return (
                <div key={index} className="chatbot-ia-sources">
                    <span>{t("chatbotIaSources")} :</span>
                    <ul>
                        {block.items.map((s) => (
                            <li key={s.url}><a href={s.url} target="_blank" rel="noopener noreferrer">{s.title || s.url}</a></li>
                        ))}
                    </ul>
                </div>
            );
        }
        if (block.type === "search_approval") {
            const decision = message.decisions[index];
            // Une demande dépassée par un message plus récent n'est plus actionnable
            const isLast = messages[messages.length - 1] && messages[messages.length - 1].id === message.id;
            return (
                <div key={index} className="chatbot-ia-confirm">
                    <p className="chatbot-ia-text mb-1">{t("chatbotIaSearchProposed")}</p>
                    <p className="chatbot-ia-text mb-1"><strong>« {block.query} »</strong></p>
                    {block.reason && <p className="chatbot-ia-info mb-2">{block.reason}</p>}
                    {!decision && isLast && (
                        <div className="d-flex gap-2">
                            <button type="button" className="btn btn-primary btn-sm" disabled={waiting} onClick={() => handleSearchDecision(message, index, true)}>
                                <i className="i-Globe me-1"></i>{t("chatbotIaSearchAllow")}
                            </button>
                            <button type="button" className="btn btn-outline-secondary btn-sm" disabled={waiting} onClick={() => handleSearchDecision(message, index, false)}>
                                {t("chatbotIaSearchRefuse")}
                            </button>
                        </div>
                    )}
                    {decision === "allowed" && <p className="chatbot-ia-decision text-success mb-0">✓ {t("chatbotIaSearchAllowed")}</p>}
                    {decision === "refused" && <p className="chatbot-ia-decision text-muted mb-0">✕ {t("chatbotIaSearchRefused")}</p>}
                </div>
            );
        }
        if (block.type === "validation") {
            // Écriture refusée : mêmes libellés que ceux de l'écran de l'article, traduits ici.
            // Le backend n'envoie que les clés : les textes n'existent que dans translations.js.
            return (
                <div key={index} className="chatbot-ia-validation">
                    <p className="mb-1"><i className="i-Danger me-1"></i>{t("chatbotIaWriteRefused")}</p>
                    <ul>
                        {block.items.map((item, i) => (
                            <li key={i}>
                                {/* Un lot peut n'être refusé que sur un article : on dit lequel */}
                                {item.article && <strong>{item.article} : </strong>}
                                {item.keys.map((key) => t(key)).join(" ")}
                            </li>
                        ))}
                    </ul>
                </div>
            );
        }
        if (block.type === "write_approval") {
            const decision = message.decisions[index];
            // Une demande dépassée par un message plus récent n'est plus actionnable
            const isLast = messages[messages.length - 1] && messages[messages.length - 1].id === message.id;
            const isExport = block.tool === TOOL_ARTICLE_EXPORT;
            const isFamille = block.tool === TOOL_FAMILLE_WRITE;
            return (
                <div key={index} className="chatbot-ia-confirm">
                    {isExport ? renderExportRecap(block.write) : isFamille ? renderFamilleRecap(block.write) : renderWriteRecap(block.write)}
                    {!decision && isLast && (
                        <div className="d-flex gap-2">
                            <button type="button" className="btn btn-primary btn-sm" disabled={waiting} onClick={() => handleWriteDecision(message, index, true)}>
                                <i className="i-Yes me-1"></i>{t("chatbotIaConfirm")}
                            </button>
                            <button type="button" className="btn btn-outline-secondary btn-sm" disabled={waiting} onClick={() => handleWriteDecision(message, index, false)}>
                                {t("chatbotIaCancel")}
                            </button>
                        </div>
                    )}
                    {/* L'export ne rend jamais le fichier ici : il apparaît dans le menu « Exports CSV » une fois prêt (même texte que le bouton d'export du site) */}
                    {decision === "confirmed" && (
                        <p className="chatbot-ia-decision text-success mb-0">
                            ✓ {t(isExport ? "csvWillBeReady" : isFamille ? "chatbotIaFamilleConfirmed" : "chatbotIaWriteConfirmed")}
                        </p>
                    )}
                    {decision === "cancelled" && <p className="chatbot-ia-decision text-muted mb-0">✕ {t("chatbotIaCancelled")}</p>}
                </div>
            );
        }
        if (block.type === "chart") {
            // Lien qui reste dans la conversation pour rouvrir le graphique (déjà affiché automatiquement
            // une première fois à sa génération) : le contenu vient tel quel du serveur, rien n'est recalculé.
            return (
                <button key={index} type="button" className="btn btn-outline-primary btn-sm chatbot-ia-link" onClick={() => setActiveChart(block.chart)} aria-label={t("chatbotIaChartReopen")}>
                    <i className="i-Line-Chart me-1"></i> {block.chart.titre}
                </button>
            );
        }
        if (block.type === "file") {
            // Fichier produit par l'assistant (CSV d'import d'articles) : jamais importé d'office,
            // l'utilisateur le télécharge, le relit, puis l'importe lui-même dans l'écran habituel
            return (
                <div key={index} className="chatbot-ia-file">
                    <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => telechargerFichier(block.nom, block.contenu)}>
                        <FontAwesomeIcon icon={faDownload} className="me-1" /> {t("chatbotIaFileDownload")} {block.nom}
                    </button>
                    <small>{t("chatbotIaFileLines").replace("{nb}", block.lignes)}</small>
                </div>
            );
        }
        if (block.type === "memory") {
            // Ce que le serveur a réellement écrit dans la mémoire (pas la phrase de l'assistant)
            return (
                <p key={index} className="chatbot-ia-info">
                    <FontAwesomeIcon icon={faBrain} className="me-1" />{t(MEMORY_EVENT_KEYS[block.action])} : « {block.texte} »
                </p>
            );
        }
        if (block.type === "link") {
            return (
                <button key={index} type="button" className="btn btn-outline-primary btn-sm chatbot-ia-link" onClick={() => handleLink(block.route)}>
                    <i className="i-Arrow-Right-in-Circle me-1"></i> {t(block.labelKey)}
                </button>
            );
        }
        if (block.type === "confirm") {
            const decision = message.decisions[index];
            return (
                <div key={index} className="chatbot-ia-confirm">
                    <p className="chatbot-ia-text mb-2">{t(block.summaryKey)}</p>
                    {!decision && (
                        <div className="d-flex gap-2">
                            <button type="button" className="btn btn-primary btn-sm" onClick={() => handleDecision(message.id, index, "confirmed")}>
                                {t("chatbotIaConfirm")}
                            </button>
                            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => handleDecision(message.id, index, "cancelled")}>
                                {t("chatbotIaCancel")}
                            </button>
                        </div>
                    )}
                    {decision === "confirmed" && <p className="chatbot-ia-decision text-success mb-0">✓ {t(block.confirmedKey)}</p>}
                    {decision === "cancelled" && <p className="chatbot-ia-decision text-muted mb-0">✕ {t("chatbotIaCancelled")}</p>}
                </div>
            );
        }
        return null;
    };

    // ─── Render ───────────────────────────────────────────────────────────────

    // Bulle fermée : le graphique reste consultable (lien dans la conversation) même si le chat est réduit
    if (!open) {
        return (
            <>
                <button type="button" className="chatbot-ia-bubble" onClick={() => setOpen(true)} aria-label={t("chatbotIaOpen")} title={t("chatbotIaOpen")}>
                    <i className="i-Speach-Bubble-3"></i>
                </button>
                <ChatbotChartModal chart={activeChart} onClose={() => setActiveChart(null)} />
            </>
        );
    }

    // Jauge de la société pour le module de configuration, jauge personnelle pour les autres
    const usageLabel = usage && usage.scope === "global" ? "chatbotIaUsage" : "chatbotIaUsagePersonal";

    return (
        <>
        <div className={"chatbot-ia-window" + (expanded ? " chatbot-ia-window-expanded" : "")} role="dialog" aria-label={t("chatbotIaTitle")}>
            {/* En-tête : titre, jauge, actions */}
            <div className={"chatbot-ia-header" + (listening ? " chatbot-ia-header-recording" : "")}>
                <div className="chatbot-ia-header-top">
                    {listening
                        ? <span className="chatbot-ia-title" role="status"><span className="chatbot-ia-rec-dot me-2"></span>{t("chatbotIaRecording")}</span>
                        : <span className="chatbot-ia-title"><i className="i-Speach-Bubble-3 me-2"></i>{t("chatbotIaTitle")}</span>}
                    <div className="chatbot-ia-actions">
                        {speechSynthesisSupported() && speaking && (
                            <button type="button" onClick={cancelSpeech} title={t("chatbotIaSpeakStop")} aria-label={t("chatbotIaSpeakStop")}>
                                <FontAwesomeIcon icon={faStop} />
                            </button>
                        )}
                        {speechSynthesisSupported() && (
                            <button type="button" className={speakReplies ? "chatbot-ia-action-on" : ""} onClick={toggleSpeakReplies} aria-pressed={speakReplies} title={speakReplies ? t("chatbotIaSpeakOff") : t("chatbotIaSpeakOn")} aria-label={speakReplies ? t("chatbotIaSpeakOff") : t("chatbotIaSpeakOn")}>
                                <FontAwesomeIcon icon={speakReplies ? faVolumeUp : faVolumeMute} />
                            </button>
                        )}
                        <button type="button" className={memoryOpen ? "chatbot-ia-action-on" : ""} onClick={toggleMemory} aria-pressed={memoryOpen} title={t("chatbotIaMemory")} aria-label={t("chatbotIaMemory")}>
                            <FontAwesomeIcon icon={faBrain} />
                        </button>
                        <button type="button" onClick={() => { cancelSpeech(); setMessages([]); setInput(""); setPieces([]); setPieceError(""); }} title={t("chatbotIaNewChat")} aria-label={t("chatbotIaNewChat")}>
                            <i className="i-Add-File"></i>
                        </button>
                        <button type="button" onClick={() => setExpanded((v) => !v)} title={expanded ? t("chatbotIaReduce") : t("chatbotIaExpand")} aria-label={expanded ? t("chatbotIaReduce") : t("chatbotIaExpand")}>
                            <i className={expanded ? "i-Minimize-Window" : "i-Maximize-Window"}></i>
                        </button>
                        <button type="button" onClick={() => setOpen(false)} title={t("chatbotIaClose")} aria-label={t("chatbotIaClose")}>
                            <i className="i-Close"></i>
                        </button>
                    </div>
                </div>
                {usage && usage.pack && (
                    <div className="chatbot-ia-usage" title={t(usageLabel)}>
                        <div className="chatbot-ia-usage-bar">
                            <div className="chatbot-ia-usage-fill" style={{ width: usage.percent + "%" }}></div>
                            {/* Fin du pack : au-delà, c'est le dépassement autorisé (jauge de la société uniquement) */}
                            {usage.marque !== null && usage.marque !== undefined && (
                                <span className="chatbot-ia-usage-mark" style={{ left: usage.marque + "%" }} title={t("chatbotIaUsagePackEnd")}></span>
                            )}
                        </div>
                        <small>{t(usageLabel)} : {formatPercent(usage.percent)} %</small>
                    </div>
                )}
            </div>

            {/* Mémoire de l'assistant : remplace la conversation, qui reste intacte derrière */}
            {memoryOpen && (
                <div className="chatbot-ia-memory">
                    <div className="chatbot-ia-memory-head">
                        <span><FontAwesomeIcon icon={faBrain} className="me-2" />{t("chatbotIaMemory")}</span>
                        {memory && <small>{t("chatbotIaMemoryCount").replace("{nb}", memory.consignes.length).replace("{max}", memory.max)}</small>}
                    </div>
                    <p className="chatbot-ia-info">{t("chatbotIaMemoryHint")}</p>
                    {memoryError && <p className="chatbot-ia-mic-error">{t(memoryError)}</p>}
                    {!memory && !memoryError && <div className="chatbot-ia-typing"><span></span><span></span><span></span></div>}
                    {memory && memory.consignes.length === 0 && <p className="chatbot-ia-text">{t("chatbotIaMemoryEmpty")}</p>}
                    {memory && memory.consignes.length > 0 && (
                        <ul className="chatbot-ia-memory-list">
                            {memory.consignes.map((consigne) => (
                                <li key={consigne.id}>
                                    <span>{consigne.content}</span>
                                    <button type="button" onClick={() => handleDeleteConsigne(consigne.id)} disabled={memoryBusy} title={t("chatbotIaMemoryDelete")} aria-label={t("chatbotIaMemoryDelete")}>
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                    <button type="button" className="btn btn-outline-secondary btn-sm mt-3" onClick={toggleMemory}>
                        <FontAwesomeIcon icon={faArrowLeft} className="me-1" /> {t("chatbotIaMemoryBack")}
                    </button>
                </div>
            )}

            {/* Conversation */}
            {!memoryOpen && (
            <div className="chatbot-ia-body">
                {messages.length === 0 && (
                    <div className="chatbot-ia-empty">
                        <p className="chatbot-ia-text">{t("chatbotIaWelcome")}</p>
                    </div>
                )}
                {messages.map((m) => (
                    <div key={m.id} className={"chatbot-ia-message chatbot-ia-message-" + m.from}>
                        {m.from === "user"
                            ? <>
                                {renderPieces(m.pieces)}
                                <p className="chatbot-ia-text mb-0">{m.text}</p>
                            </>
                            : m.blocks.map((block, i) => renderBlock(m, block, i))}
                    </div>
                ))}
                {waiting && <div className="chatbot-ia-message chatbot-ia-message-bot chatbot-ia-typing"><span></span><span></span><span></span></div>}
                <div ref={bottomRef}></div>
            </div>
            )}

            {/* Saisie */}
            {!memoryOpen && (
            <div className="chatbot-ia-footer">
                {/* Pièces choisies, envoyées avec le prochain message */}
                {renderPieces(pieces, removePiece)}
                <div className="chatbot-ia-input">
                    <textarea ref={inputRef} rows={1} maxLength={MAX_MESSAGE_LENGTH} value={input} placeholder={t("chatbotIaPlaceholder")} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown} />
                    <input ref={fileInputRef} type="file" accept="application/pdf,image/*" multiple hidden onChange={handleFiles} />
                    <button type="button" className="btn chatbot-ia-attach" onClick={() => fileInputRef.current && fileInputRef.current.click()} disabled={waiting || piecesRestantes <= 0} aria-label={t("chatbotIaAttach")} title={piecesRestantes <= 0 ? t("chatbotIaFileTooMany") : t("chatbotIaAttach")}>
                        <FontAwesomeIcon icon={faPaperclip} />
                    </button>
                    {/* Micro masqué si le navigateur n'a pas de reconnaissance vocale (ex. Firefox) */}
                    {getSpeechRecognition() && (
                        <button type="button" className={"btn chatbot-ia-mic" + (listening ? " chatbot-ia-mic-active" : "")} onClick={toggleDictation} disabled={waiting} aria-pressed={listening} aria-label={listening ? t("chatbotIaMicStop") : t("chatbotIaMicStart")} title={listening ? t("chatbotIaMicStop") : t("chatbotIaMicStart")}>
                            <i className="i-Microphone-3"></i>
                        </button>
                    )}
                    <button type="button" className="btn btn-primary" onClick={() => handleSend()} disabled={waiting || (!input.trim() && !pieces.length)} aria-label={t("chatbotIaSend")} title={t("chatbotIaSend")}>
                        <i className="i-Arrow-Right"></i>
                    </button>
                </div>
                {micError && <p className="chatbot-ia-mic-error mb-0">{micError}</p>}
                {pieceError && <p className="chatbot-ia-mic-error mb-0">{pieceError}</p>}
            </div>
            )}
        </div>
        <ChatbotChartModal chart={activeChart} onClose={() => setActiveChart(null)} />
        </>
    );
};

export default ChatbotIa;
